<template>
	<section class="">
		<!-- Кнопка «Назад» над заголовком (конвенция проекта — button--card button--m)
		     → на предыдущую страницу (шагов у входа нет). Если возвращаться некуда
		     (прямой заход, разлогин с закрытой страницы) — ведёт на главную.
		     Логотип живёт только в панели. -->
		<button class="button button--card button--m d-inline-flex flex-v-center gap-small mn-b-medium align-self-start" @click="goBack">
			<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1em" width="1em"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 480 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-370.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"></path></svg>
			{{ backTarget.isHome && step !== 'code' ? t('auth.signUp.home') : t('auth.signUp.back') }}
		</button>

		<h3 class="mn-b-medium">
			{{ t('auth.signIn.greeting') }}
			<br>
			<span class="fw-semi">{{ t('auth.signIn.subtitle') }}</span>
		</h3>

		<!-- Вход по коду (`code` у идентификатора в `options.auth`): после почты —
		     ввод кода из письма/SMS, как шаг `code` в SignUp. Код введён целиком —
		     EnterCode сам зовёт onVerifyCode. Назад к почте ведёт кнопка «Назад»
		     наверху. -->
		<template v-if="step === 'code'">
			<EnterCode
				v-model="code"
				:identifier="tabAuth === 'email' ? auth.state.user.email : auth.state.user.phone"
				:is-email="tabAuth === 'email'"
				:submit="onVerifyCode"
				:resend="onResendCode"
			/>
		</template>

		<!-- Шаг `identifier`: вкладки, почта/телефон, пароль. На шаге кода его нет,
		     как в SignUp. -->
		<template v-else>
		<!-- Select -->
		<div
			v-if="availableTabs.length > 1"
			class="mn-b-small"
		>
			<Tab
				v-model:selected="tabAuth"
				:tabs="availableTabs"
				classTabBar="w-100"
			/>
		</div>
		<!-- Form -->
		<div class="pos-relative">
		<!-- Phone -->
			<transition name="slide-fade">
				<div v-show="tabAuth === 'phone'" class="mn-b-small">
					<FieldPhone
						:model-value="auth.state.user.phone"
						@change="(event) => auth.state.user.phone = event"
						:dropdownOptions="{
							showDialCodeInSelection: true,
							showFlags: true,
							showDialCodeInList: true,
							tabIndex: -1
						}"
						:validCharactersOnly="true"
						:validation="phoneValidation"
						mode="national"
						:inputOptions="{placeholder: t('auth.signIn.phonePlaceholder'), type: 'tel'}"
						class="input mn-b-thin"
					/>

				</div>
			</transition>
			<transition name="slide-fade">
				<div v-show="tabAuth === 'email'" class="mn-b-small">
					<Field
						v-model:field="auth.state.user.email"
						:placeholder="t('auth.signIn.emailPlaceholder')"
						:validation="emailValidation"
						:tabIndex="-1"
						class="input"
					/>
				</div>
			</transition>
		</div>

			<!-- Пароль — если он включён у идентификатора. Пароль и код вместе:
			     пароль на форме, код — ссылкой под ним (как у Logto: первый способ
			     основной, второй — ссылкой). Только код — поля пароля нет, «Войти»
			     шлёт код. -->
			<template v-if="method.password">
				<Field
					v-model:field="auth.state.user.password"
					type="password"
					:validation="passswordValidation"
					:placeholder="t('auth.signIn.passwordPlaceholder')"
					class="input mn-b-small"
				/>
				<!-- Links -->
				<div class="w-100 mn-b-medium d-flex flex-column gap-thin">
					<router-link
						:to="{name: 'Reset Password', query: { returnUrl: route.query?.returnUrl}}"
						class="underline d-block t-blue"
					>
						{{ t('auth.signIn.forgotPassword') }}
					</router-link>
					<a v-if="method.code" href="#" class="underline d-block t-blue" @click.prevent="onSendCode()">
						{{ t('auth.signIn.loginByCode') }}
					</a>
				</div>
			</template>
			<!-- Button -->
			<Button
				:submit="method.password ? onSubmit : onSendCode"
				class="w-100 button--accent"
			>
				{{ t('auth.signIn.signin') }}
			</Button>
		</template>

		<!-- Ссылка на регистрацию — прибита к низу карточки -->
		<div class="w-100 mn-t-auto pd-t-medium">
			<router-link :to="{name: 'Sign Up', query: { returnUrl: route.query?.returnUrl}}" class="underline d-block t-blue">{{ t('auth.signIn.signup') }}</router-link>
		</div>

		<!-- <Button
			:submit="onSubmitApple"
			:callback="redirectTo"
			class="mn-b-thin bg-inverse t-inverse"
		>
			{{ t('auth.signIn.signin_apple') }}
		</Button> -->
	</section>
</template>

<script setup>
// Import components
import Tab           from '@martyrs/src/design-system/components/Tab/Tab.vue'
import Field         from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone    from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Button        from '@martyrs/src/design-system/components/Button/Button.vue'
import EnterCode     from '@martyrs/src/modules/auth/views/components/blocks/EnterCode.vue'
// Import libs
import { computed, onMounted, ref, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
// Import store
import { useStore, setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { mixins } from '@martyrs/src/modules/core/core.client.js'
import { auth } from '@martyrs/src/modules/auth/views/store/auth.js'
// Import validation
import * as inputsValidation from '@martyrs/src/modules/auth/views/validations/inputs.validation'
// Import utils
import { resolveAuthBackTarget } from '@martyrs/src/modules/auth/views/utils/back-target.js'
import { resolveAuthMethods } from '@martyrs/src/modules/auth/views/utils/auth-methods.js'

// Get store
const store = useStore()
const core = store.core
const { isModuleInstalled } = mixins.useGlobalMixins()

// Init validation
const phoneValidation = ref(null)
const passswordValidation = ref(null)
const emailValidation = ref(null)
// Accessing router
const route = useRoute()
const router = useRouter()
// Localization
const { t } = useI18n({
	useScope: 'global'
})
// Куда ведёт кнопка над заголовком. Считаем один раз при открытии страницы:
// история после монтирования компонента уже не меняется.
const backTarget = resolveAuthBackTarget(router)

// На шаге кода «Назад» возвращает к почте, как шаги в SignUp.
function goBack() {
	if (step.value === 'code') backToIdentifier()
	else if (backTarget.isHome) router.push(backTarget.path)
	else router.back()
}
// Способы входа проекта: вкладки — включённые идентификаторы, форма — чем
// подтверждать вход по выбранному (`utils/auth-methods.js`).
const methods = resolveAuthMethods(core.state?.options?.auth)

const availableTabs = computed(() => Object.keys(methods).map(value => ({ name: t(`auth.signIn.${value}`), value })));

const tabAuth = ref(availableTabs.value.length ? availableTabs.value[0].value : '');

const method = computed(() => methods[tabAuth.value] || { password: false, code: false });

// Идентификатор из query (?email= / ?phone=): человек пришёл со страницы
// регистрации, где адрес уже оказался занят. Подставляем его и открываем нужную
// вкладку — остаётся ввести пароль. Читаем в setup: FieldPhone берёт modelValue
// на своём монтировании. Email вперёд телефона — как на регистрации.
for (const type of ['email', 'phone']) {
	const raw = typeof route.query?.[type] === 'string' ? route.query[type].trim() : '';
	if (!raw) continue;
	if (!availableTabs.value.some(tab => tab.value === type)) continue;
	auth.state.user[type] = raw;
	tabAuth.value = type;
	break;
}

const loadExternalScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

onMounted(async () => {
	await loadExternalScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
});
// `identifier` — форма входа, `code` — ввод кода (EnterCode) после почты,
// challenge_id — от ответа requires_otp.
const step = ref('identifier')
const code = ref('')
const challenge_id = ref(null)
const codeValidation = ref(null)

// Methods
async function validateIdentifier() {
	try {
	  if (tabAuth.value === 'phone') await inputsValidation.validateInputs(
			phoneValidation,
			inputsValidation.validatePhone,
			auth.state.user.phone,
			t('auth.validation.phone')
		)
		if (tabAuth.value === 'email') await inputsValidation.validateInputs(
			emailValidation,
			inputsValidation.validateEmail,
			auth.state.user.email,
			t('auth.validation.email')
		)
	} catch (error) {
		console.log(error)
		throw new Error
	}
}

// Тело запроса входа по коду: только выбранный идентификатор, без пароля.
function codePayload() {
	return {
		email: tabAuth.value === 'email' ? auth.state.user.email : undefined,
		phone: tabAuth.value === 'phone' ? auth.state.user.phone : undefined,
	}
}

async function onSubmit() {
	await validateIdentifier()

	// COSTIL PLEASE GOD MAKE IT GOOD, FUKEN INPUT COMPONENT RETURN OBJECT NUT NUMBER
	const userCopy = {...auth.state.user};
	// COSTIL PLEASE GOD MAKE IT GOOD, FUKEN INPUT COMPONENT RETURN OBJECT NUT NUMBER

	await auth.actions.login(userCopy, tabAuth.value)

	redirectTo()
}

// Вход по коду («Войти» без пароля или ссылка «Войти по коду»): первый запрос
// без кода — сервер шлёт код и отвечает
// requires_otp. Ошибку loginCode уже показал через setError (как login);
// повторная отправка из ResendCode получает её наверх и сама решает,
// таймер это или тост.
async function onSendCode({ silent = false } = {}) {
	await validateIdentifier()
	const result = await auth.actions.loginCode(codePayload(), tabAuth.value)
	if (result?.status === 'requires_otp' && result.challenge?.challengeId) {
		challenge_id.value = result.challenge.challengeId
		code.value = ''
		step.value = 'code'
		return
	}
	if (!silent) setError({ response: { data: { errorCode: 'OTP_NOT_REQUESTED' } } })
	throw new Error('OTP_NOT_REQUESTED')
}

function onResendCode() {
	return onSendCode({ silent: true })
}

// Код введён целиком — второй запрос с challengeId и otp. Успех — тот же
// переход, что после входа по паролю. Ошибку показывает loginCode (setError).
async function onVerifyCode() {
	await inputsValidation.validateInputs(
		codeValidation,
		// validateInputs ждёт от валидатора объект { status, message }, а не boolean.
		(v) => ({ status: typeof v === 'string' && v.trim().length >= 4, message: t('auth.validation.codeFromEmail') }),
		code.value,
		t('auth.validation.codeFromEmail')
	)
	try {
		await auth.actions.loginCode({
			...codePayload(),
			challengeId: challenge_id.value,
			otp: code.value.trim(),
		}, tabAuth.value)
	} catch (error) {
		// Неверный код — очистить ячейки, человек остаётся на шаге кода.
		const code_errors = ['INVALID_CODE', 'CODE_NOT_FOUND', 'ALREADY_USED']
		if (code_errors.includes(error?.response?.data?.errorCode)) code.value = ''
		throw error
	}

	redirectTo()
}

function backToIdentifier() {
	step.value = 'identifier'
	code.value = ''
	challenge_id.value = null
}

async function onSubmitApple() {
  try {
    window.AppleID.auth.init({
      clientId: 'com.thecommune.app.signin',
      scope: 'email name',
      redirectURI: 'https://thecommunephuket.com/auth/signin',
      usePopup: true,
    });

    let response = await window.AppleID.auth.signIn();

    if (response) {
      console.log(response);
      await auth.actions.login(response, 'apple');
    }

  } catch (error) {
    console.error(error);
    throw new Error('Error during Apple authentication');
  }
}

function redirectTo () {
	if (!auth.state.user._id) {
		throw new Error('AUTH_STATE_NOT_INITIALIZED')
	}

	if (route.query?.returnUrl) {
		// Прокидываем весь query (кроме returnUrl) на целевую страницу: флоу
		// вроде ?buyPack=… должны пережить вход, а не теряться здесь.
		const { returnUrl, ...extraQuery } = route.query
		router.push({
			path: returnUrl,
			query: {
				...extraQuery,
				afterAuth: 'true'
			}
		})
	} else if (auth.state.access.roles?.includes('ROLE_ADMIN') && isModuleInstalled('backoffice')) {
		router.push({
			path: '/backoffice',
			query: {
				afterAuth: 'true'
			}
		})
	} else {
		// Дефолт пост-входа — из конфига проекта (options.auth.redirectAfterAuth),
		// иначе профиль пользователя (дефолт martyrs).
		const configured = core.state?.options?.auth?.redirectAfterAuth
		if (configured) {
			router.push({
				path: configured,
				query: {
					afterAuth: 'true'
				}
			})
		} else {
			router.push({
				name: 'User Profile',
				params: {
					_id: auth.state.user._id
				},
				query: {
					afterAuth: 'true'
				}
			})
		}
	}
}
</script>

<style lang="scss">
	.input-error {
	  border: 1px solid red;
	  box-shadow: 0 0 3px 0 red;
	}
</style>